//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { GridRowsProp } from '@mui/x-data-grid-premium'
import { useOtChainState, useOtSelector } from '@react/redux/store'
import { EthAddr } from '@shared/SchemaTypes'
import { PoolOverviewCTOSchema } from '@shared/sharedTypes'
import React, { useEffect, useState } from 'react'
import * as yup from 'yup'

import { PoolTypeEnum } from '@shared/Enums'

import { fetchDailyPoolOverviewCTOs } from '@react/fetchers/PoolFetchers'
import { logOT } from '@shared/loggingShared'
import { ReportDataShape } from '@shared/ReportDefinitionCTO'
import dayjs, { Dayjs } from 'dayjs'
import ReportExplorer from './ReportExplorer'

export const LenderExplorerSchema = yup.object({
  poolOverviewCTO: PoolOverviewCTOSchema,
})

export default function PoolOverviewTimePeriodPageFlex(): React.ReactElement {
  const [loading, setLoading] = useState<boolean>(false)
  const [fetchDataError, setFetchDataError] = useState<any | undefined>(undefined)
  const {
    stageConfiguration: { restAPIKey, serverApiBaseUrl },
    chainConfiguration: { chainName },
  } = useOtChainState()
  // const poolFlexOverviewCTO =useAppSelector<RootStoreState, PoolFlexOverviewCTO | undefined>(
  //   (state) => state.poolState.poolOverviewCTOs[poolAddr ?? ETH_ADDR_ZERO] as PoolFlexOverviewCTO
  // )

  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | undefined>(undefined)

  const [poolAddr, setPoolAddr] = useState<EthAddr | undefined>(undefined)
  const [pools, setPools] = useState<string[]>([])
  const [rows, setRows] = useState<GridRowsProp>([])
  const poolBaseCTOs = useOtSelector((state) => state.poolState.poolOverviewCTOs)

  useEffect(function initializeDatesEffect() {
    if (chainName === 'LocalHardhat') {
      const startDate = dayjs.tz('2024-11-01', 'UTC').startOf('day')
      const endDate = dayjs.tz('2024-11-30', 'UTC').startOf('day')
      setDateRangeFromExplorer([startDate, endDate])
    } else {
      const startDate = dayjs().tz('UTC').startOf('day')
      const endDate = dayjs().tz('UTC').startOf('day')
      setDateRangeFromExplorer([startDate, endDate])
    }
  }, [])

  useEffect(() => {
    if (poolBaseCTOs) {
      const poolAddresses = Object.values(poolBaseCTOs)
        .filter((pool) => pool.poolType === PoolTypeEnum.FlexibleRate)
        .map((pool) => pool.poolAddr)
      logOT.debug('poolAddresses', poolAddresses)
      setPools(poolAddresses)
    }
  }, [poolBaseCTOs])

  const refreshData = () => {
    async function buildRowsEffect(): Promise<void> {
      try {
        if (poolAddr === undefined) {
          return
        }

        setLoading(true)
        setFetchDataError(undefined)
        const startDayStr = dateRange && dateRange.length > 1 ? dateRange[0].format('YYYY-MM-DD') : undefined
        const endDayStr = dateRange && dateRange.length > 1 ? dateRange[1].format('YYYY-MM-DD') : undefined
        const poolOverviewCTOs = await fetchDailyPoolOverviewCTOs(
          serverApiBaseUrl,
          restAPIKey,
          poolAddr,
          startDayStr,
          endDayStr
        )
        logOT.debug('poolOverviewCTOs', poolOverviewCTOs)
        setRows(poolOverviewCTOs)
      } catch (e: any) {
        setFetchDataError(e)
      } finally {
        setLoading(false)
      }
    }
    buildRowsEffect()
  }

  function setDateRangeFromExplorer(dateRange: [Dayjs, Dayjs]): void {
    setRows([])
    setDateRange(dateRange)
  }

  return (
    <>
      <ReportExplorer
        loading={loading}
        fetchDataError={fetchDataError}
        reportDataShape={ReportDataShape.POOL_OVERVIEW_FLEX}
        rows={rows}
        dateRange={dateRange}
        setDateRange={setDateRangeFromExplorer}
        poolAddr={poolAddr}
        setPoolAddr={setPoolAddr}
        poolAddrs={pools}
        refreshData={refreshData}
      />
    </>
  )
}
